import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ComponentProps, type ReactNode, type SubmitEvent } from 'react';
import { ActionGlyph, type ActionIcon } from './icons.tsx';
import { useScreen } from './scope.ts';
import { strings } from './strings.ts';
import { buttonVariants } from './ui/button.tsx';
import { inputBox, inputClass } from './ui/input.tsx';

/**
 * The few controls every page uses. Every one keeps the label, the hint
 * and the error tied to the input (`aria-describedby`, `aria-invalid`), tap
 * targets at least 44 px, and a visible focus ring (styles.css).
 *
 * A field owns its error: a page hands it a Problem when a check fails, and
 * the field hides it as soon as the person edits, until the page hands it a
 * new one. No page clears an error in its onChange.
 */

/** Something wrong with what a person entered. A new object per check, so the same words shown again are a new problem. */
export interface Problem {
  readonly message: string;
}

export const fieldProblem = (message: string): Problem => ({ message });

/** The error a field shows: the page's problem, until the person edits. */
export function useOwnedError(error: Problem | undefined): {
  readonly shown: string | undefined;
  readonly dismiss: () => void;
} {
  const [dismissed, setDismissed] = useState<Problem>();
  return {
    shown: error !== undefined && error !== dismissed ? error.message : undefined,
    dismiss: () => {
      setDismissed(error);
    },
  };
}

/** `danger` ends or removes something: MIX's destructive, late-ink words, never the brand. On a kit screen the variants are shadcn's button (ui/button.tsx). */
type Variant = 'primary' | 'secondary' | 'quiet' | 'danger';
const buttonClass = (kit: boolean, variant: Variant, wide: boolean, className: string | undefined, main = false) =>
  kit ? buttonVariants({ variant, wide, size: main ? 'main' : 'default' }) : ['button', `button-${variant}`, wide && 'button-block', className].filter(Boolean).join(' ');

export function Button({
  variant = 'primary',
  busy = false,
  busyLabel,
  wide = false,
  main = false,
  icon,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The one action the screen is for: the main control height. */
  readonly main?: boolean;
  readonly variant?: Variant;
  readonly busy?: boolean;
  readonly busyLabel?: string;
  /** The full width of its column. */
  readonly wide?: boolean;
  /** A picture before the words, on a kit screen. */
  readonly icon?: ActionIcon;
}) {
  const kit = useScreen() !== null;
  return (
    <button {...rest} className={buttonClass(kit, variant, wide, className, main)} disabled={busy || rest.disabled} aria-busy={busy || undefined}>
      {busy ? <span className={kit ? 'size-s4 animate-spin rounded-chip border-2 border-current border-r-transparent' : 'spinner'} aria-hidden="true" /> : kit && icon !== undefined && <ActionGlyph icon={icon} />}
      <span>{busy && busyLabel !== undefined ? busyLabel : children}</span>
    </button>
  );
}

export function ButtonLink({
  variant = 'primary',
  wide = false,
  className,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  readonly variant?: Variant;
  readonly wide?: boolean;
}) {
  return <a {...rest} className={buttonClass(useScreen() !== null, variant, wide, className)} />;
}

interface FieldProps {
  readonly label: string;
  readonly optionalLabel?: string;
  readonly hint?: string;
  readonly error?: Problem | undefined;
}

/** How wide a field's box is: the column (`full`), or about ten characters for a number or a code (`short`). */
export type FieldWidth = 'full' | 'short' | 'code' | 'phone';
/** `code`: a one-time code, its digits large and spaced. `phone`: the one field of a sign-up step, large. */
const FIELD_WIDTH = {
  full: `${inputClass} w-full`,
  short: `${inputClass} w-full max-w-[10rem]`,
  code: `${inputBox} h-s7 w-full max-w-[14rem] font-figures text-heading font-bold tracking-[0.3em] tabular-nums`,
  phone: `${inputBox} h-s7 w-full text-heading font-semibold tabular-nums`,
} as const satisfies Record<FieldWidth, string>;
const INPUT = inputClass;

export function TextField({ label, optionalLabel, hint, error: problemIn, width = 'full', onChange, ...input }: FieldProps & { readonly width?: FieldWidth } & ComponentProps<'input'>) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const kit = useScreen() !== null;
  const { shown: error, dismiss } = useOwnedError(problemIn);
  const describedBy = [hint !== undefined && hintId, error !== undefined && errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={kit ? 'flex flex-col gap-s2' : 'field'}>
      <label className={kit ? 'text-body font-semibold text-ink' : 'label'} htmlFor={id}>
        {label}
        {optionalLabel !== undefined && <span className={kit ? 'font-normal text-muted' : 'label-optional'}>{strings.common.optional(optionalLabel)}</span>}
      </label>
      {hint !== undefined && (
        <p className={kit ? 'text-caption text-wrap text-muted' : 'hint'} id={hintId}>
          {hint}
        </p>
      )}
      <input
        {...input}
        id={id}
        className={kit ? FIELD_WIDTH[width] : ['input', input.className].filter(Boolean).join(' ')}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          dismiss();
          onChange?.(event);
        }}
      />
      {error !== undefined && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
}

/** A field's label on a kit screen: the words, and "(optional)" quieter. */
function LabelWords({ label, optionalLabel }: { readonly label: string; readonly optionalLabel: string | undefined }) {
  return (
    <>
      {label}
      {optionalLabel !== undefined && <span className="font-normal text-muted">{strings.common.optional(optionalLabel)}</span>}
    </>
  );
}

const LABEL = 'text-body font-semibold text-ink';
/** A hint fills its field's width line by line (`text-wrap: wrap`), not the balanced wrap a Chinese page sets for prose. */
const HINT = 'text-caption text-wrap text-muted';

/**
 * A fieldset of controls that answer one question (a birthday's day and
 * month): legend, hint, and one error for the group, owned like a field's
 * (hidden as soon as any control in it changes). Its controls get
 * `aria-invalid` from `invalid`.
 */
export function FieldGroup({
  legend,
  optionalLabel,
  hint,
  error: problemIn,
  className,
  children,
}: {
  readonly legend: string;
  readonly optionalLabel?: string;
  readonly hint?: string;
  readonly error?: Problem | undefined;
  readonly className?: string;
  readonly children: (invalid: boolean) => ReactNode;
}) {
  const id = useId();
  const kit = useScreen() !== null;
  const { shown: error, dismiss } = useOwnedError(problemIn);
  if (kit)
    return (
      <fieldset className="min-w-0" aria-describedby={hint === undefined ? undefined : `${id}-hint`} onChange={dismiss}>
        <legend className={`pb-s2 ${LABEL}`}>
          <LabelWords label={legend} optionalLabel={optionalLabel} />
        </legend>
        <div className="flex flex-col gap-s3">
          {hint !== undefined && (
            <p className={`-mt-s1 ${HINT}`} id={`${id}-hint`}>
              {hint}
            </p>
          )}
          {children(error !== undefined)}
          {error !== undefined && <FieldError>{error}</FieldError>}
        </div>
      </fieldset>
    );
  return (
    <fieldset className={['field', className].filter(Boolean).join(' ')} aria-describedby={hint === undefined ? undefined : `${id}-hint`} onChange={dismiss}>
      <legend className="label">
        {legend}
        {optionalLabel !== undefined && <span className="label-optional">{strings.common.optional(optionalLabel)}</span>}
      </legend>
      {hint !== undefined && (
        <p className="hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {children(error !== undefined)}
      {error !== undefined && <FieldError>{error}</FieldError>}
    </fieldset>
  );
}

/**
 * A list of short lines a person edits one at a time (a voucher's terms and
 * conditions): each line its own box with a button that removes it, and a
 * button that adds one, up to `max`, focused as it appears. The page reads
 * the lines as they are and drops the blank ones itself.
 */
export function LinesField({
  legend,
  optionalLabel,
  hint,
  error,
  lines,
  onLines,
  max,
  maxLength,
  words,
}: {
  readonly legend: string;
  readonly optionalLabel?: string;
  readonly hint?: string;
  readonly error?: Problem | undefined;
  readonly lines: readonly string[];
  readonly onLines: (lines: readonly string[]) => void;
  readonly max: number;
  readonly maxLength: number;
  /** A line's name to a screen reader (`line(2)`), its remove button's, and the add button. */
  readonly words: { readonly line: (n: number) => string; readonly remove: (n: number) => string; readonly add: string };
}) {
  const kit = useScreen() !== null;
  const list = useRef<HTMLOListElement>(null);
  const added = useRef(false);
  useEffect(() => {
    if (!added.current) return;
    added.current = false;
    list.current?.querySelector<HTMLTextAreaElement>('li:last-child textarea')?.focus();
  }, [lines.length]);
  return (
    <FieldGroup legend={legend} {...(optionalLabel !== undefined && { optionalLabel })} {...(hint !== undefined && { hint })} error={error}>
      {(invalid) => (
        <>
          {lines.length > 0 && (
            <ol ref={list} className={kit ? 'flex flex-col gap-s2' : undefined}>
              {lines.map((line, at) => (
                // A line has no identity but its place; the boxes are controlled, so a removal shows the right words in each.
                <li key={at} className={kit ? 'flex items-start gap-s2' : undefined}>
                  {/* A box that grows with its words (field-sizing), so a long line reads whole on a phone; a line holds no line break. */}
                  <textarea
                    className={kit ? `${inputBox} min-h-control min-w-0 flex-1 resize-none py-s2 text-field leading-normal [field-sizing:content]` : 'input'}
                    rows={1}
                    aria-label={words.line(at + 1)}
                    aria-invalid={invalid || undefined}
                    autoComplete="off"
                    maxLength={maxLength}
                    value={line}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.preventDefault();
                    }}
                    onChange={(event) => {
                      const typed = event.target.value.replace(/\s*\n\s*/g, ' ');
                      onLines(lines.map((other, index) => (index === at ? typed : other)));
                    }}
                  />
                  <button
                    type="button"
                    className={kit ? buttonVariants({ variant: 'ghost', size: 'icon' }) : 'button button-quiet'}
                    aria-label={words.remove(at + 1)}
                    onClick={() => {
                      onLines(lines.filter((_, index) => index !== at));
                    }}
                  >
                    <X aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>
          )}
          {lines.length < max && (
            <div>
              <Button
                type="button"
                variant="secondary"
                icon="plus"
                onClick={() => {
                  added.current = true;
                  onLines([...lines, '']);
                }}
              >
                {words.add}
              </Button>
            </div>
          )}
        </>
      )}
    </FieldGroup>
  );
}

export interface Choice<V extends string> {
  readonly value: V;
  readonly label: string;
}

/**
 * One question answered by picking: one of the options (radios), or with
 * `multiple` any of them (checkboxes), handed back in the options' order.
 * `chips` lays short words side by side as pills; `list` puts longer ones
 * one per line, a box or a dot before each. Legend, hint and error as a
 * FieldGroup's.
 */
export function ChoiceField<V extends string>(
  props: {
    readonly legend: string;
    readonly optionalLabel?: string;
    readonly hint?: string;
    readonly error?: Problem | undefined;
    readonly look: 'chips' | 'list';
    readonly options: readonly Choice<V>[];
  } & (
    | {
        readonly multiple: true;
        readonly value: readonly V[];
        readonly onValue: (value: readonly V[]) => void;
      }
    | {
        readonly multiple?: false;
        readonly value: V;
        readonly onValue: (value: V) => void;
      }
  ),
) {
  const name = useId();
  const picked = (value: V) => (props.multiple === true ? props.value.includes(value) : props.value === value);
  const pick = (value: V, on: boolean) => {
    if (props.multiple === true) props.onValue(props.options.map((option) => option.value).filter((each) => (each === value ? on : props.value.includes(each))));
    else if (on) props.onValue(value);
  };
  const type = props.multiple === true ? 'checkbox' : 'radio';
  return (
    <FieldGroup
      legend={props.legend}
      {...(props.optionalLabel !== undefined && {
        optionalLabel: props.optionalLabel,
      })}
      {...(props.hint !== undefined && { hint: props.hint })}
      error={props.error}
    >
      {(invalid) => (
        <div className={props.look === 'chips' ? 'flex flex-wrap gap-s2' : 'flex flex-col'}>
          {props.options.map((option) =>
            props.look === 'chips' ? (
              <label
                key={option.value}
                className="relative inline-flex h-control min-w-control cursor-pointer items-center justify-center rounded-chip border border-line-strong bg-surface px-s4 font-medium whitespace-nowrap text-ink-2 hover:bg-sunk has-checked:border-brand has-checked:bg-brand-soft has-checked:text-brand has-checked:hover:bg-brand-soft has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-brand"
              >
                <input
                  className="absolute inset-0 cursor-pointer appearance-none rounded-chip focus-visible:outline-none"
                  type={type}
                  name={name}
                  checked={picked(option.value)}
                  aria-invalid={invalid || undefined}
                  onChange={(event) => {
                    pick(option.value, event.target.checked);
                  }}
                />
                <span>{option.label}</span>
              </label>
            ) : (
              <Tick
                key={option.value}
                type={type}
                name={name}
                label={option.label}
                checked={picked(option.value)}
                aria-invalid={invalid || undefined}
                onChange={(event) => {
                  pick(option.value, event.target.checked);
                }}
              />
            ),
          )}
        </div>
      )}
    </FieldGroup>
  );
}

/** A box (or a dot, for a radio) and its words, the whole line a target. */
function Tick({ label, type, ...input }: { readonly label: string; readonly type: 'checkbox' | 'radio' } & Omit<ComponentProps<'input'>, 'type'>) {
  const id = useId();
  const round = type === 'radio' ? 'rounded-chip' : 'rounded-control';
  return (
    <label htmlFor={id} className="flex min-h-control cursor-pointer items-center gap-s3 text-body text-ink">
      <span className="relative inline-flex size-s5 shrink-0">
        <input
          {...input}
          id={id}
          type={type}
          className={`peer size-s5 cursor-pointer appearance-none border border-line-strong bg-surface ${round} checked:border-brand checked:bg-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
        />
        {type === 'checkbox' ? (
          <svg viewBox="0 0 16 16" className="pointer-events-none absolute inset-0 m-auto hidden size-s4 text-on-brand peer-checked:block" aria-hidden="true">
            <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span className="pointer-events-none absolute inset-0 m-auto hidden size-s2 rounded-chip bg-on-brand peer-checked:block" aria-hidden="true" />
        )}
      </span>
      <span>{label}</span>
    </label>
  );
}

/**
 * A select whose first option names it ("Day"), so its label is for screen
 * readers only. `grow`: its share of a row of selects (default 1), so a
 * longer value ("September") gets the room a shorter one ("29") does not need.
 */
export function Select({
  label,
  grow = 1,
  children,
  ...select
}: {
  readonly label: string;
  readonly grow?: number;
  readonly children: ReactNode;
} & ComponentProps<'select'>) {
  const id = useId();
  if (useScreen() !== null)
    return (
      <div className="relative min-w-0 flex-1" style={{ flexGrow: grow }}>
        <label className="sr-only" htmlFor={id}>
          {label}
        </label>
        <select {...select} id={id} className={`${INPUT} w-full cursor-pointer appearance-none pr-s6`}>
          {children}
        </select>
        <svg viewBox="0 0 16 16" className="pointer-events-none absolute top-0 right-s3 bottom-0 my-auto size-s4 text-ink-2" aria-hidden="true">
          <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  return (
    <div className="select-field">
      <label className="visually-hidden" htmlFor={id}>
        {label}
      </label>
      <div className="select-wrap">
        <select {...select} id={id} className="select">
          {children}
        </select>
      </div>
    </div>
  );
}

/** A select that answers a question on its own, with its label shown and an error it owns, as a TextField does. */
export function SelectField({
  label,
  optionalLabel,
  error: problemIn,
  children,
  onChange,
  ...select
}: {
  readonly label: string;
  readonly optionalLabel?: string;
  readonly error?: Problem | undefined;
  readonly children: ReactNode;
} & ComponentProps<'select'>) {
  const id = useId();
  const errorId = `${id}-error`;
  const kit = useScreen() !== null;
  const { shown: error, dismiss } = useOwnedError(problemIn);
  const control = (
    <select
      {...select}
      id={id}
      className={kit ? `${INPUT} w-full cursor-pointer appearance-none pr-s7` : 'select'}
      aria-invalid={error !== undefined || undefined}
      aria-describedby={error === undefined ? undefined : errorId}
      onChange={(event) => {
        dismiss();
        onChange?.(event);
      }}
    >
      {children}
    </select>
  );
  if (kit)
    return (
      <div className="flex flex-col gap-s2">
        <label className={LABEL} htmlFor={id}>
          <LabelWords label={label} optionalLabel={optionalLabel} />
        </label>
        <div className="relative">
          {control}
          <svg viewBox="0 0 16 16" className="pointer-events-none absolute top-0 right-s3 bottom-0 my-auto size-s4 text-ink-2" aria-hidden="true">
            <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {error !== undefined && <FieldError id={errorId}>{error}</FieldError>}
      </div>
    );
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="select-wrap">{control}</div>
      {error !== undefined && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
}

/** Lines of text in one box (one term per line), with its label, hint and an error it owns, as a TextField does. Kit screens only. */
export function TextAreaField({ label, optionalLabel, hint, error: problemIn, onChange, ...area }: FieldProps & ComponentProps<'textarea'>) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const { shown: error, dismiss } = useOwnedError(problemIn);
  const describedBy = [hint !== undefined && hintId, error !== undefined && errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-s2">
      <label className={LABEL} htmlFor={id}>
        <LabelWords label={label} optionalLabel={optionalLabel} />
      </label>
      {hint !== undefined && (
        <p className={HINT} id={hintId}>
          {hint}
        </p>
      )}
      <textarea
        {...area}
        id={id}
        className="min-h-s8 w-full resize-y rounded-control border border-line-strong bg-surface px-s3 py-s2 text-field leading-normal text-ink focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand disabled:cursor-default disabled:bg-sunk disabled:text-muted aria-invalid:border-late-ink"
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          dismiss();
          onChange?.(event);
        }}
      />
      {error !== undefined && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
}

export function Checkbox({ label, ...input }: { readonly label: string } & Omit<ComponentProps<'input'>, 'type'>) {
  const id = useId();
  if (useScreen() !== null) return <Tick {...input} type="checkbox" label={label} />;
  return (
    <label className="checkbox" htmlFor={id}>
      <input {...input} id={id} type="checkbox" />
      <span className="checkbox-box" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="14" height="14">
          <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="checkbox-label">{label}</span>
    </label>
  );
}

export function FieldError({ id, children }: { readonly id?: string; readonly children: ReactNode }) {
  const kit = useScreen() !== null;
  return (
    <p className={kit ? 'flex items-center gap-s2 text-body font-medium text-late-ink' : 'field-error'} id={id}>
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="currentColor" />
        <path d="M8 4.5v4.2M8 11.2v.3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span>{children}</span>
    </p>
  );
}

const NOTICE = {
  error: 'border-late-soft bg-late-soft text-late-ink',
  warning: 'border-warn-line bg-warn-soft text-warn-ink',
  info: 'border-line bg-surface text-ink',
} as const;

/** A message about the whole form. `role="alert"` so a screen reader hears it when it appears. */
export function Notice({ tone, children, action }: { readonly tone: 'error' | 'warning' | 'info'; readonly children: ReactNode; readonly action?: ReactNode }) {
  const kit = useScreen() !== null;
  return (
    <div className={kit ? `flex flex-wrap items-center justify-between gap-s3 rounded-control border px-s4 py-s3 font-medium ${NOTICE[tone]}` : `notice notice-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <p>{children}</p>
      {action}
    </div>
  );
}

/**
 * A form with its one submit: it stops the browser's own submit and check,
 * shows the form's failure above the button, busy while the answer comes.
 * The button is full width, or with `also` (more buttons about the form)
 * its own width with them beside it. Fields own their errors (TextField).
 */
export function Form({
  label,
  onSubmit,
  submit,
  busy = false,
  busyLabel,
  failure,
  also,
  submitVariant = 'primary',
  children,
}: {
  /** The form's name for a screen reader, when the screen holds more than one. */
  readonly label?: string;
  readonly onSubmit: () => void;
  readonly submit: string;
  readonly busy?: boolean;
  readonly busyLabel?: string;
  readonly failure?: string | undefined;
  readonly also?: ReactNode;
  /** `secondary` when the form is not the screen's main action. */
  readonly submitVariant?: 'primary' | 'secondary';
  readonly children: ReactNode;
}) {
  return (
    <form
      className="flex flex-col gap-s4"
      aria-label={label}
      noValidate
      onSubmit={(event: SubmitEvent) => {
        event.preventDefault();
        if (!busy) onSubmit();
      }}
    >
      {children}
      {failure !== undefined && <Notice tone="error">{failure}</Notice>}
      {also === undefined ? (
        <Button type="submit" variant={submitVariant} busy={busy} {...(busyLabel !== undefined && { busyLabel })} wide>
          {submit}
        </Button>
      ) : (
        <div className="flex flex-wrap items-center gap-s3">
          <Button type="submit" variant={submitVariant} busy={busy} {...(busyLabel !== undefined && { busyLabel })}>
            {submit}
          </Button>
          {also}
        </div>
      )}
    </form>
  );
}

/**
 * One of a few cards picked by a tap (a voucher at the counter): a radio
 * per card, the picked one drawn as the kiosk's selected chip, a 3 px
 * action-red edge on the soft ground.
 */
export function CardChoice<V extends string>({
  legend,
  value,
  onValue,
  options,
}: {
  readonly legend: string;
  readonly value: V | null;
  readonly onValue: (value: V) => void;
  readonly options: readonly { readonly value: V; readonly card: ReactNode }[];
}) {
  const name = useId();
  return (
    <fieldset className="min-w-0">
      <legend className={`pb-s3 ${LABEL}`}>{legend}</legend>
      <div className="flex flex-col gap-s3">
        {options.map((option) => (
          <label
            key={option.value}
            className="relative block cursor-pointer rounded-card has-checked:[&>article]:bg-brand-soft has-checked:[&>article]:outline-3 has-checked:[&>article]:-outline-offset-2 has-checked:[&>article]:outline-brand has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-brand"
          >
            <input
              type="radio"
              name={name}
              className="sr-only"
              checked={option.value === value}
              onChange={() => {
                onValue(option.value);
              }}
            />
            {option.card}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * A picture a person picks from their device: its preview (or a dashed empty
 * frame), a button that opens the file picker, and one that takes it off.
 * The page uploads what `onPick` hands it and passes the stored picture back
 * as `src`; `accept` is the content types the upload slot takes.
 */
export function PictureField({ label, hint, src, accept, pickLabel, removeLabel, busy = false, busyLabel, error, onPick, onRemove }: { readonly label: string; readonly hint?: string; readonly src: string | null; readonly accept: readonly string[]; readonly pickLabel: string; readonly removeLabel: string; readonly busy?: boolean; readonly busyLabel?: string; readonly error?: string; readonly onPick: (file: File) => void; readonly onRemove: () => void }) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-s2" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="text-body font-semibold text-ink">
        {label}
      </span>
      {hint !== undefined && <p className="text-caption text-wrap text-muted">{hint}</p>}
      <div className="flex items-center gap-s4">
        <div className="flex size-[5rem] shrink-0 items-center justify-center overflow-hidden rounded-control border-2 border-dashed border-line bg-sunk">
          {src !== null && <img src={src} alt={label} className="size-full object-cover" data-testid="picture-preview" />}
        </div>
        <div className="flex flex-col items-start gap-s2">
          <Button type="button" variant="secondary" busy={busy} {...(busyLabel !== undefined && { busyLabel })} onClick={() => input.current?.click()}>
            {pickLabel}
          </Button>
          {src !== null && (
            <Button type="button" variant="quiet" disabled={busy} onClick={onRemove}>
              {removeLabel}
            </Button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept={accept.join(',')}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file !== undefined) onPick(file);
          }}
        />
      </div>
      {error !== undefined && <FieldError>{error}</FieldError>}
    </div>
  );
}
